import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Calendar, Eye, ArrowRight, Newspaper } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BlogPost } from '@/types';
import { useTranslation } from 'react-i18next';

interface BlogCardProps {
  post: BlogPost;
  className?: string;
}

export function BlogCard({ post, className = '' }: BlogCardProps) {
  const { t, i18n } = useTranslation();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language || 'pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const [imagemFalhou, setImagemFalhou] = useState(false);

  return (
    <Card data-testid="blog-card" className={`group overflow-hidden card-interactive bg-card border-border h-full flex flex-col ${className}`}>
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        {/* Notícia sem banner: fundo neutro em vez de imagem quebrada com o texto alternativo. */}
        {post.image && !imagemFalhou ? (
          <img
            src={post.image}
            alt={post.title}
            onError={() => setImagemFalhou(true)}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/20 via-card to-card">
            <Newspaper className="h-12 w-12 text-primary/40" aria-hidden="true" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
        
        {/* Category Badge */}
        <Badge 
          className="absolute top-3 left-3 bg-primary/90 text-primary-foreground"
        >
          {post.category}
        </Badge>
        
        {/* Date */}
        <div className="absolute bottom-3 left-3 flex items-center gap-1 text-xs text-foreground/90">
          <Calendar className="w-3 h-3" />
          <span>{formatDate(post.date)}</span>
        </div>
      </div>

      {/* Content */}
      <CardContent className="p-4 flex-1 flex flex-col">
        <h3 className="font-display font-semibold text-lg text-foreground group-hover:text-primary transition-colors line-clamp-2">
          {post.title}
        </h3>
        <p className="text-sm text-muted-foreground mt-2 line-clamp-2 flex-1">
          {post.excerpt}
        </p>
        
        {/* Footer */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
          <Link
            to={`/noticias/${post.slug ?? post.id}`}
            data-testid="blog-card-read-more"
            className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary-hover transition-colors"
          >
            {t('store.blog_card.read_more')}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Eye className="w-3 h-3" />
            <span>{post.views}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default BlogCard;
